/**
 * SubscriptionService - Handles subscription and subscription plan management operations
 * Based on API_DOCUMENTATION_SUBSCRIPTION_MANAGEMENT.md
 */

import type { HttpClient } from '../api/HttpClient';
import type {
  SubscriptionPlan,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
  SubscriptionPlanQueryParams,
  SubscriptionPlansData,
  Subscription,
  SubscriptionCreateRequest,
} from '../../types/api/subscription';
import type { ApiResponse } from '../../types/api/common';

export class SubscriptionService {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  // ========== User Subscription Management ==========

  /**
   * Create a subscription for a user (Admin grants subscription)
   * POST /admin/subscriptions/create
   */
  async createSubscription(request: SubscriptionCreateRequest): Promise<Subscription> {
    const response = await this.httpClient.post<Subscription>(
      '/admin/subscriptions/create',
      request
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create subscription');
    }

    return response.data;
  }

  // ========== Subscription Plan Management ==========

  /**
   * List all subscription plans with optional filters
   * GET /admin/subscription-plans
   */
  async getSubscriptionPlans(params: SubscriptionPlanQueryParams = {}): Promise<SubscriptionPlansData> {
    const queryParams = new URLSearchParams();

    if (params.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params.tierGranted) queryParams.append('tierGranted', params.tierGranted);
    if (params.durationMonths !== undefined) queryParams.append('durationMonths', String(params.durationMonths));
    if (params.isFeatured !== undefined) queryParams.append('isFeatured', String(params.isFeatured));
    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);

    const url = `/admin/subscription-plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await this.httpClient.get<SubscriptionPlansData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch subscription plans');
    }

    return response.data;
  }

  /**
   * Get subscription plan by ID
   * GET /admin/subscription-plans/{planId}
   */
  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan> {
    const response = await this.httpClient.get<SubscriptionPlan>(
      `/admin/subscription-plans/${planId}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch subscription plan');
    }

    return response.data;
  }

  /**
   * Create a new subscription plan
   * POST /admin/subscription-plans
   */
  async createSubscriptionPlan(request: SubscriptionPlanCreateRequest): Promise<SubscriptionPlan> {
    const response = await this.httpClient.post<SubscriptionPlan>(
      '/admin/subscription-plans',
      request
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create subscription plan');
    }

    return response.data;
  }

  /**
   * Update an existing subscription plan
   * PUT /admin/subscription-plans/{planId}
   */
  async updateSubscriptionPlan(
    planId: string,
    request: SubscriptionPlanUpdateRequest
  ): Promise<SubscriptionPlan> {
    const response = await this.httpClient.put<SubscriptionPlan>(
      `/admin/subscription-plans/${planId}`,
      request
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update subscription plan');
    }

    return response.data;
  }

  /**
   * Activate a subscription plan
   * PUT /admin/subscription-plans/{planId}/activate
   */
  async activateSubscriptionPlan(planId: string): Promise<SubscriptionPlan> {
    const response = await this.httpClient.put<SubscriptionPlan>(
      `/admin/subscription-plans/${planId}/activate`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to activate subscription plan');
    }

    return response.data;
  }

  /**
   * Deactivate a subscription plan
   * PUT /admin/subscription-plans/{planId}/deactivate
   */
  async deactivateSubscriptionPlan(planId: string): Promise<SubscriptionPlan> {
    const response = await this.httpClient.put<SubscriptionPlan>(
      `/admin/subscription-plans/${planId}/deactivate`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to deactivate subscription plan');
    }

    return response.data;
  }

  /**
   * Delete a subscription plan (soft delete)
   * DELETE /admin/subscription-plans/{planId}
   */
  async deleteSubscriptionPlan(planId: string): Promise<{ message: string }> {
    const response = await this.httpClient.delete<ApiResponse<string>>(
      `/admin/subscription-plans/${planId}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete subscription plan');
    }

    return { message: response.message || 'Subscription plan deleted successfully' };
  }
}

